function HSMMDebounce(func, wait) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

const LG_BREAKPOINT = 1024, DEBOUNCE_DELAY = 250;

class HSMegaMenu extends HTMLElement {
    #megamenuIsVisible = this.dataset.showMegamenu === "true";

    #sectionId = this.dataset.sectionId;
    #section = document.querySelector(`#shopify-section-${this.#sectionId}`);

    #megamenus = document.querySelectorAll(".hs-megamenu");
    #megamenuOpenBtn = document.querySelector(".hs-megamenu-open");
    #megamenuCloseBtn = document.querySelector(".hs-megamenu-close");
    #megamenuMainLinks = this.querySelector(".hs-megamenu-main-links");

    #backdrop = document.querySelector(".hs-backdrop-megamenu");

    constructor() {
        super();

        if (!this.#megamenuIsVisible) return;

        // Store the bound event handlers in instance variables
        this.boundHandleMouseOver = this.#handleMouseOver.bind(this);
        this.boundHandleMouseLeave = this.#handleMouseLeave.bind(this);
    }

    connectedCallback() {
        // Handle scroll event
        this.#handleScroll();
        window.addEventListener("scroll", this.#handleScroll.bind(this));

        if (!this.#megamenuIsVisible) return;

        // Added this function because of error for IOS devices
        // resource https://stackoverflow.com/questions/17710893/why-when-do-i-have-to-tap-twice-to-trigger-click-on-ios
        this.#toggleMegaMenuListeners(window.innerWidth <= LG_BREAKPOINT);
        const handleResize = HSMMDebounce(() => this.#toggleMegaMenuListeners(window.innerWidth <= LG_BREAKPOINT), DEBOUNCE_DELAY);

        // Add the event listener for window resize
        window.addEventListener('resize', handleResize);

        // Handle Mega Menu Click
        this.addEventListener("click", this.#handleMegamenuClick.bind(this));

        // Handle Mega Menu open/close on mobile
        this.#megamenuOpenBtn.addEventListener("click", this.#handleMegamenuOpen.bind(this));
        this.#megamenuCloseBtn.addEventListener("click", this.#handleMegamenuClose.bind(this));

        // Handle backdrop click
        this.#backdrop.addEventListener("click", this.#handleMegamenuClose.bind(this));
    }


    #toggleMegaMenuListeners(remove = false) {
        if (remove) {
            // Handle Mega Menu Open
            this.removeEventListener("mouseover", this.boundHandleMouseOver);
            // Handle Mega Menu Close
            this.#megamenus.forEach(megamenu => megamenu.removeEventListener("mouseleave", this.boundHandleMouseLeave));
        } else {
            // Handle Mega Menu Open
            this.addEventListener("mouseover", this.boundHandleMouseOver);
            // Handle Mega Menu Close
            this.#megamenus.forEach(megamenu => megamenu.addEventListener("mouseleave", this.boundHandleMouseLeave));

            // Close mobile megamenu if window size > 1024
            this.#handleMegamenuClose();
        }
    }

    #handleMouseOver(evt) {
        const el = evt.target;
        if (el.dataset.linkHandle) return this.#showMegamenu(el);
    }

    #showMegamenu(link) {
        this.#hideMegamenus();
        this.#removeActiveLinkClass();

        const linkHandle = link.dataset.linkHandle;

        const megamenu = document.querySelector(`.hs-megamenu[data-menu-handle="${linkHandle}"]`);
        if (!megamenu) return;

        link.classList.add("active");

        this.#section.classList.add("bg-white");
        this.#backdrop.classList.remove("hidden");
        megamenu.classList.remove("hidden");
    }

    #hideMegamenus() {
        this.#megamenus.forEach(megamenu => megamenu.classList.add("hidden"));
        this.#backdrop.classList.add("hidden");
    }

    #handleMouseLeave(evt) {
        evt.currentTarget.classList.add("hidden")
        this.#backdrop.classList.add("hidden");
        this.#section.classList.remove("bg-white");

        this.#removeActiveLinkClass();
    }

    #handleScroll(evt) {
        // 50px scroll threshold
        const scrollAmount = window.scrollY || window.pageYOffset;
        if (scrollAmount > 50) this.#section.classList.remove("active");
        else this.#section.classList.add("active");
    }

    #handleMegamenuClick(evt) {
        const el = evt.target;

        if (el.dataset.linkHandle && el.tagName !== "A") return this.#handleMobileMegamenuOpen(el);
        if (el.dataset.level && el.tagName !== "A") return this.#handleSubmenuOpen(el);
        if (el.classList.contains("hs-megamenu-back")) return (() => {
            this.#hideMegamenus();
            this.#megamenuMainLinks.classList.remove("hidden");
        })();
    }

    #handleSubmenuOpen(link) {
        const level = link.dataset.level;

        let links = link.parentElement.querySelectorAll(`li[data-level="${level}"]`);

        link.setAttribute("data-closed", link.dataset.closed === "true" ? "false" : "true");

        links.forEach(li => li.classList.toggle("max-lg:hidden"));
    }

    #handleMegamenuOpen() {
        this.#megamenuCloseBtn.classList.remove("hidden");
        this.#megamenuOpenBtn.classList.add("hidden");
        this.#megamenuMainLinks.classList.remove("hidden");
        this.#megamenuMainLinks.classList.add("flex");
        this.#backdrop.classList.remove("hidden");
        this.#section.classList.add("bg-white");
        this.#hideMegamenus();
    }

    #handleMegamenuClose() {
        this.#megamenuCloseBtn.classList.add("hidden");
        this.#megamenuOpenBtn.classList.remove("hidden");
        this.#megamenuMainLinks.classList.add("hidden");
        this.#megamenuMainLinks.classList.remove("flex");
        this.#backdrop.classList.add("hidden");
        this.#section.classList.remove("bg-white");
        this.#hideMegamenus();

        this.#removeActiveLinkClass();
    }

    #handleMobileMegamenuOpen(el) {
        this.#showMegamenu(el);
        this.#megamenuMainLinks.classList.add("hidden");
    }

    #removeActiveLinkClass() {
        const activeLink = this.querySelector(".hs-megamenu-main-link.active");
        activeLink?.classList.remove("active");
    }
}

customElements.define("hs-megamenu", HSMegaMenu);
